import { BarChart3, ChevronDown, RefreshCw } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useD3LineChart, type LineConfig } from "../../../hooks/useD3LineChart";
import adminUserActivityService, {
  USER_ACTIVITY_DEFAULT_DAYS,
  USER_ACTIVITY_MAX_DAYS,
  type UserActivityBucket,
  type UserActivityPeriod,
} from "../../../services/adminUserActivityService";
import { formatNumber } from "../../../utils/numberUtils";

const ACTIVITY_SERIES: Array<LineConfig & { dataKey: keyof UserActivityBucket["counts"] }> = [
  { dataKey: "all", label: "All clients (distinct)", stroke: "var(--rp-amber)", strokeWidth: 3, swatchClassName: "bg-rp-amber" },
  { dataKey: "web", label: "Web", stroke: "var(--rp-chart-3)", strokeWidth: 2, strokeDasharray: "6 3", swatchClassName: "bg-rp-chart-3" },
  { dataKey: "ios", label: "iOS", stroke: "var(--rp-chart-4)", strokeWidth: 2, strokeDasharray: "2 3", swatchClassName: "bg-rp-chart-4" },
  { dataKey: "android", label: "Android", stroke: "var(--rp-chart-2)", strokeWidth: 2, strokeDasharray: "4 3", swatchClassName: "bg-rp-chart-2" },
  { dataKey: "chatgpt", label: "ChatGPT", stroke: "var(--rp-chart-6)", strokeWidth: 2, strokeDasharray: "0", swatchClassName: "bg-rp-chart-6" },
  { dataKey: "gemini", label: "Gemini", stroke: "var(--rp-chart-5)", strokeWidth: 2, strokeDasharray: "7 3", swatchClassName: "bg-rp-chart-5" },
  { dataKey: "claude", label: "Claude", stroke: "var(--rp-chart-7)", strokeWidth: 2, strokeDasharray: "2 3", swatchClassName: "bg-rp-chart-7" },
  { dataKey: "other_mcp", label: "Other MCP", stroke: "var(--rp-chart-1)", strokeWidth: 2, strokeDasharray: "1 4", swatchClassName: "bg-rp-chart-1" },
];

function formatBucketDate(date: Date, period: UserActivityPeriod, short = false): string {
  return date.toLocaleString("en-US", {
    timeZone: "UTC",
    ...(!short || period === "daily" ? { month: "short" as const, day: "numeric" as const } : {}),
    ...(!short ? { year: "numeric" as const } : {}),
    ...(period === "hourly" ? { hour: "2-digit" as const, minute: "2-digit" as const, hourCycle: "h23" as const } : {}),
  });
}

function ActivityChart({ items, period }: { items: UserActivityBucket[]; period: UserActivityPeriod }) {

  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);
  const visibleSeries = useMemo(() => ACTIVITY_SERIES.filter((series) => !hiddenSeries.includes(series.dataKey)), [hiddenSeries]);
  const data = useMemo(() => items.map((item) => ({
    date: item.datetime,
    fullDate: item.datetime,
    ...item.counts,
  })), [items]);
  const xAxisTicks = useMemo(() => items.map((item) => ({
    value: item.datetime,
    label: formatBucketDate(new Date(item.datetime), period, true),
  })), [items, period]);
  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    data,
    lines: visibleSeries,
    isDarkMode: false,
    height: 280,
    margins: { top: 12, right: 60, bottom: 12, left: 8 },
    yAxisMin: 0,
    yAxisFormatter: (value) => Number.isInteger(value) ? formatNumber(value, 0) : "",
    tooltipFormatter: (value) => formatNumber(value, 0),
    tooltipDateFormatter: (date) => `${formatBucketDate(date, period)} UTC`,
    maxGapMs: (period === "hourly" ? 1 : 24) * 60 * 60 * 1000,
    showLegend: false,
    showEndLabels: false,
    renderingMode: "enhanced",
    useUtcDateLabels: true,
    xAxisTicks,
  });

  return (
    <>
      <ul aria-label={`${period} activity legend`} className="mb-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-v7-ink-soft">
        {ACTIVITY_SERIES.map((series) => (
          <li key={series.dataKey}>
            <button
              type="button"
              aria-pressed={!hiddenSeries.includes(series.dataKey)}
              onClick={() => setHiddenSeries((hidden) => hidden.includes(series.dataKey)
                ? hidden.filter((key) => key !== series.dataKey)
                : [...hidden, series.dataKey])}
              className={`flex items-center gap-2 rounded-v7-control px-1 py-1 transition-colors hover:bg-v7-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark ${hiddenSeries.includes(series.dataKey) ? "opacity-50 line-through" : ""}`}
            >
              <span aria-hidden="true" className={`h-0.5 w-5 rounded-full ${series.swatchClassName}`} />
              {series.label}
            </button>
          </li>
        ))}
      </ul>
      <div ref={containerRef} className="relative h-[280px] w-full">
        {visibleSeries.length > 0 ? (
          <>
            <svg ref={svgRef} className="block" role="img" aria-label={`${period} API-active users in UTC. Exact values are available in the data table below.`} />
            <div ref={tooltipRef} aria-hidden="true" className="pointer-events-none absolute z-20 hidden rounded-v7-panel border border-v7-ink/15 bg-v7-white px-3 py-2 shadow-lg" />
          </>
        ) : (
          <p role="status" className="flex h-full items-center justify-center text-sm text-v7-ink/60">Select a legend to show activity.</p>
        )}
      </div>
      <details className="mt-3 text-sm text-v7-ink-soft">
        <summary className="cursor-pointer rounded-v7-control focus:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark">
          View {period} data table
        </summary>
        <div className="mt-3 max-h-64 overflow-auto rounded-v7-panel border border-v7-ink/15" tabIndex={0} role="region" aria-label={`${period} activity data`}>
          <table className="w-full min-w-[950px] text-sm">
            <caption className="sr-only">{period} API-active users by period start in UTC</caption>
            <thead className="sticky top-0 bg-v7-paper">
              <tr className="border-b border-v7-ink/15">
                <th scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold">Period start (UTC)</th>
                {ACTIVITY_SERIES.map((series) => <th key={series.dataKey} scope="col" className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold">{series.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.datetime} className="border-b border-v7-ink/15">
                  <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-normal">
                    <time dateTime={item.datetime}>{formatBucketDate(new Date(item.datetime), period)}</time>
                  </th>
                  {ACTIVITY_SERIES.map((series) => <td key={series.dataKey} className="px-3 py-2 text-right font-mono">{formatNumber(item.counts[series.dataKey], 0)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}

type ReportState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; items: UserActivityBucket[] };

function ActivityReport({ period, days }: { period: UserActivityPeriod; days: number }) {
  const [state, setState] = useState<ReportState>({ status: "loading" });
  const [revision, setRevision] = useState(0);
  const title = period === "hourly" ? "Hourly activity" : "Daily activity";
  const windowLabel = period === "hourly" ? "Latest 24 UTC hours" : `Latest ${formatNumber(days, 0)} UTC days`;

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const report = period === "hourly"
          ? await adminUserActivityService.getHourly(controller.signal)
          : await adminUserActivityService.getDaily(days, controller.signal);
        if (!controller.signal.aborted) setState({ status: "loaded", items: report.items });
      } catch {
        if (!controller.signal.aborted) setState({ status: "error" });
      }
    };
    void load();
    return () => controller.abort();
  }, [period, days, revision]);

  const reload = () => {
    setState({ status: "loading" });
    setRevision((value) => value + 1);
  };

  return (
    <section aria-label={title} className="min-w-0 rounded-v7-panel border border-v7-ink/15 bg-v7-white p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold leading-tight text-v7-ink">{title}</h3>
          <p className="mt-1 text-xs text-v7-ink/60">{windowLabel}; {period === "hourly" ? "current hour" : "today"} can still accumulate activity.</p>
        </div>
        <button type="button" onClick={reload} disabled={state.status === "loading"} aria-label={`Refresh ${period} activity`} className="rounded-v7-panel border border-v7-ink/15 bg-v7-white p-2 text-v7-ink-soft transition-colors hover:border-v7-ink/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark disabled:opacity-50">
          <RefreshCw aria-hidden="true" className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} />
        </button>
      </div>
      {state.status === "loading" ? (
        <div role="status" className="flex h-80 items-center justify-center rounded-v7-panel bg-v7-paper text-sm text-v7-ink-soft">
          Loading {period} activity...
        </div>
      ) : state.status === "error" ? (
        <div role="alert" className="rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-4 text-sm text-v7-rust">
          <p>Could not load {period} activity.</p>
          <button type="button" onClick={reload} className="mt-3 rounded-v7-panel bg-v7-ink px-3 py-2 font-semibold text-v7-white transition-colors hover:bg-v7-bronze-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark">Retry {period} activity</button>
        </div>
      ) : state.items.length === 0 ? (
        <div role="status" className="rounded-v7-popover border border-v7-ink/15 bg-v7-white py-24 text-center">
          <BarChart3 aria-hidden="true" className="mx-auto mb-3 h-8 w-8 text-v7-ink/60" />
          <p className="text-sm text-v7-ink-soft">No recorded {period} activity in this window.</p>
        </div>
      ) : (
        <>
          <ActivityChart items={state.items} period={period} />
          <p className="mt-3 text-xs text-v7-ink/60">
            {formatNumber(state.items.length, 0)} recorded {period === "hourly" ? "hour" : "day"}{state.items.length === 1 ? "" : "s"}. Missing periods are omitted; gaps do not mean zero users.
          </p>
        </>
      )}
    </section>
  );
}

function ActivityPanelContent({ initiallyExpanded }: { initiallyExpanded: boolean }) {
  const [days, setDays] = useState(USER_ACTIVITY_DEFAULT_DAYS);
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const contentId = useId();
  return (
    <section aria-label="User activity by client" className="mb-6 flex-shrink-0 space-y-4 transition-colors duration-300">
      <h2 className="text-lg font-bold text-v7-ink">
        <button type="button" aria-expanded={isExpanded} aria-controls={contentId} onClick={() => setIsExpanded((expanded) => !expanded)} className="flex w-full items-center justify-between gap-3 rounded-v7-panel py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark">
          User activity by client
          <ChevronDown aria-hidden="true"className={`h-4 w-4 text-v7-ink/60 transition-transform ${isExpanded ?"rotate-180" : ""}`} />
        </button>
      </h2>
      <div id={contentId} hidden={!isExpanded}>
        {isExpanded && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="max-w-3xl">
                <p className="mt-1 text-sm text-v7-ink-soft">
                  Unique users within each UTC hour or day. A user can appear in multiple clients; the all-client total counts them once. ChatGPT includes Codex; Claude includes Code and hosted connectors. Authenticated background requests also count.
                </p>
                <p className="mt-1 text-xs text-v7-ink/60">Activity across all clients, independent of user-list filters. Hourly counts cannot be added to derive daily unique users.</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-v7-ink-soft">
                Daily window
                <select value={days} onChange={(event) => setDays(Number(event.target.value))} className="rounded-v7-panel border border-v7-ink/15 bg-v7-white px-3 py-2 text-sm text-v7-ink transition-all focus:border-v7-bronze/30 focus:outline-none focus:ring-1 focus:ring-v7-bronze-dark">
                  {[7, USER_ACTIVITY_DEFAULT_DAYS, 90, USER_ACTIVITY_MAX_DAYS].map((value) => <option key={value} value={value}>{formatNumber(value, 0)} days</option>)}
                </select>
              </label>
            </div>
            <p className="text-xs text-v7-ink/60">MCP history begins when tracking starts. Earlier zero counts do not prove there was no MCP usage.</p>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,28rem),1fr))] gap-4">
              <ActivityReport period="hourly" days={1} />
              <ActivityReport key={days} period="daily" days={days} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default function UserActivityPanel({ initiallyExpanded = false }: { initiallyExpanded?: boolean } = {}) {
  const { user } = useAuth();
  return user?.roles?.includes("ADMIN") ? <ActivityPanelContent key={user.id} initiallyExpanded={initiallyExpanded} /> : null;
}
